import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { successHandler } from '../../../../utils/handlers';
import {
  AN_PN_COLORS,
  BUSINESS_UNIT_COLORS,
  currencyFormatter,
} from '../../utils/dashboardConstants';

function PieGraph({ id, data }) {
  const _series = [];
  const amount = [];
  const colors = [];
  const labels = [];
  const labelsCopy = [];
  const negativeService = [];
  const isAtNeedPreNeed = ['At-Need', 'Pre-Need'].includes(data.segmentation[0]?.serviceType);

  data.segmentation.forEach((service) => {
    if (service.totalAmount && service.totalAmount > 0) {
      const p = ((service.totalAmount / data.totalAmount) * 100).toFixed(2);
      _series.push(+p);
      amount.push(service.totalAmount);
      labels.push(`${service.serviceType} - ${p}%`);
      labelsCopy.push(service.serviceType);
      negativeService.push(service.isNegative);
      colors.push(
        isAtNeedPreNeed
          ? AN_PN_COLORS[service.serviceType]
          : BUSINESS_UNIT_COLORS[service.serviceType],
      );
    }
  });

  const handleDownloadMenu = (event) => {
    const parent = document.getElementById(id);
    if (parent) {
      const child = parent.querySelector('.apexcharts-toolbar');
      if (child && !child.contains(event.target)) {
        const menu = parent.querySelector('.apexcharts-menu');
        menu?.classList.remove('apexcharts-menu-open');
      }
      const svgDownload = parent.querySelector('.exportSVG');
      const pngDownload = parent.querySelector('.exportPNG');
      // Handle download popup notification
      if (svgDownload === event.target || pngDownload === event.target) {
        successHandler({
          message: {
            message: 'Graph is exported successfully',
            title: 'Success',
          },
        });
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleDownloadMenu);
    return () => {
      document.removeEventListener('click', handleDownloadMenu);
    };
  }, [id]);

  const options = {
    chart: {
      type: 'pie',
      toolbar: {
        show: true,
        export: {
          csv: { filename: data.title },
          svg: { filename: data.title },
          png: { filename: data.title },
        },
      },
      animations: {
        enabled: false,
      },
      selection: { enabled: false },
    },
    plotOptions: {
      pie: { expandOnClick: false },
    },
    states: {
      normal: {
        filter: { type: 'none', value: 0 },
      },
      hover: {
        filter: { type: 'lighten', value: 0.1 },
      },
      active: {
        filter: { type: 'none', value: 0 },
        allowMultipleDataPointsSelection: false,
      },
    },
    labels,
    amount,
    legend: {
      show: true,
      position: 'bottom',
      fontWeight: 600,
    },
    colors,
    stroke: { width: 0 },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      custom: ({ seriesIndex, w }) => {
        const _amount = negativeService[seriesIndex]
          ? -w.config.amount[seriesIndex]
          : w.config.amount[seriesIndex];
        const html = `
        <html>
        <body class='tooltip-body'>
        <style>
          .tooltip-body {
            background-color: transparent;
          }
          .tooltip-container {
            color: #000;
            width: fit-content;
            display: grid;
            gap: 0px;
            grid-template-columns: auto auto;
            border-radius: 7px;
            border: 2px solid #ECECEC;
          }
          .tooltip-service {
            margin: 0px;
            padding: 5px;
			      background-color: #ECECEC;
          }
          .tooltip-amount {
            margin: 0px;
            padding: 5px;
            background-color: #FFF;
          }
          .ts-number, .ts-number {
            text-align: right;
          }
        </style>
        <div class='tooltip-container'>
          <p class='tooltip-service'>${labelsCopy[seriesIndex]}:</p>
          <p class='tooltip-service ts-number'>${_series[seriesIndex]}%</p>
          <p class='tooltip-amount'>Amount:</p>
          <p class='tooltip-amount ts-number'>${currencyFormatter(_amount)}</p>
          </div>
        </body>
        </html>`;
        return html;
      },
    },
    fill: {
      colors,
    },
  };

  return (
    <div className="bar-graph-continer pie-graph-container" id={id}>
      <ReactApexChart options={options} series={_series} type="pie" height={350} />
    </div>
  );
}

PieGraph.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    isCollections: PropTypes.bool,
    segmentation: PropTypes.shape([]).isRequired,
    totalAmount: PropTypes.number.isRequired,
  }).isRequired,
};

export default memo(PieGraph);
